import { Box, Heading, HStack, Icon, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Skeleton, Text, VStack } from '@chakra-ui/react';
import { MdHub } from 'react-icons/md';
import { useQuery } from 'react-query';
import { IContrat } from '@/service/club/types';
import { Label } from '@/components/atoms/Label';
import { Conexao, getConexoes } from '../services/conexoes.api';

function formatCadastro(value?: string | null): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  if (!match) return '';
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function formatCep(value?: string | null): string {
  const digits = (value ?? '').replace(/\D/g, '');
  if (digits.length !== 8) return value || '';
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

export function sameContract(conexao: Conexao, contrato?: IContrat): boolean {
  const left = conexao.contrato == null ? '' : String(conexao.contrato).trim();
  const right = contrato?.numero_contrato == null ? '' : String(contrato.numero_contrato).trim();
  return left !== '' && left === right;
}

function StatusChip({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <HStack spacing={1.5} px="8px" py="3px" borderRadius="full" backgroundColor={bg} flexShrink={0}>
      <Box boxSize="6px" borderRadius="full" backgroundColor={color} />
      <Text fontSize="11px" fontWeight="600" color={color}>
        {label}
      </Text>
    </HStack>
  );
}

export function useConexoes(enabled: boolean) {
  const query = useQuery(['conexoes'], () => getConexoes(), {
    enabled,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const conexoes = query.data?.result === false ? [] : query.data?.Conexoes ?? [];
  const failed = query.isError || query.data?.result === false;
  const errorMessage =
    (query.isError && (query.error as { response?: { data?: { error?: string } } })?.response?.data?.error) ||
    query.data?.error ||
    'Não foi possível consultar as conexões.';

  return {
    conexoes,
    isLoading: enabled && query.isLoading,
    failed,
    errorMessage,
  };
}

interface ConexoesModalProps {
  isOpen: boolean;
  onClose: () => void;
  contrato?: IContrat;
  colors: { primaryColor: string };
}

export function ConexoesModal({ isOpen, onClose, contrato, colors }: ConexoesModalProps) {
  const { conexoes, isLoading, failed, errorMessage } = useConexoes(isOpen);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent maxW={{ base: '95vw', md: '720px' }} borderRadius="xl" overflow="hidden" boxShadow="xl">
        <ModalHeader py={6} px={6} borderBottom="1px solid" borderColor="gray.100" backgroundColor="gray.50">
          <HStack spacing={3}>
            <Box p={2} borderRadius="lg" backgroundColor={`${colors.primaryColor}15`} color={colors.primaryColor}>
              <Icon as={MdHub} boxSize={6} />
            </Box>
            <VStack align="flex-start" spacing={0}>
              <Heading size="md" color="gray.800" fontWeight="600">
                Conexões
              </Heading>
              <Label fontSize="sm" color="gray.500" fontWeight="400">
                {failed
                  ? errorMessage
                  : isLoading
                    ? 'Carregando conexões...'
                    : `${conexoes.length} ${conexoes.length === 1 ? 'conexão' : 'conexões'}`}
              </Label>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={4} right={4} size="lg" borderRadius="full" />
        <ModalBody py={6} px={6} maxHeight={{ base: '70vh', md: '65vh' }} overflowY="auto">
          {failed ? (
            <VStack py={12} spacing={4}>
              <Icon as={MdHub} boxSize={12} color="gray.300" />
              <Label color="gray.500" textAlign="center">
                {errorMessage}
              </Label>
            </VStack>
          ) : isLoading ? (
            <VStack spacing={3}>
              <Skeleton height="120px" borderRadius="xl" />
              <Skeleton height="120px" borderRadius="xl" />
            </VStack>
          ) : conexoes.length === 0 ? (
            <VStack py={12} spacing={4}>
              <Icon as={MdHub} boxSize={12} color="gray.300" />
              <Label color="gray.500">Nenhuma conexão encontrada.</Label>
            </VStack>
          ) : (
            <VStack spacing={3} align="stretch">
              {conexoes.map((conexao) => {
                const bloqueada = conexao.bloqueada === 'Sim';
                const reduzida = conexao.esta_reduzida === 'Sim';
                const desteContrato = sameContract(conexao, contrato);
                const meta = [
                  conexao.cep ? `CEP ${formatCep(conexao.cep)}` : '',
                  conexao.cadastro ? formatCadastro(conexao.cadastro) : '',
                ]
                  .filter(Boolean)
                  .join(' · ');

                return (
                  <Box
                    key={conexao.codconexao}
                    border="1px solid"
                    borderColor={desteContrato ? colors.primaryColor : 'gray.200'}
                    borderRadius="xl"
                    px="16px"
                    py="14px"
                    backgroundColor={desteContrato ? `${colors.primaryColor}08` : 'white'}
                  >
                    <Text fontSize="14px" fontWeight="600" color="gray.800" noOfLines={2}>
                      {conexao.endereco}
                    </Text>
                    {meta && (
                      <Text fontSize="12px" color="gray.500" marginTop="4px">
                        {meta}
                      </Text>
                    )}
                    <HStack spacing={2} marginTop="8px" flexWrap="wrap">
                      {bloqueada && <StatusChip label="Bloqueada" bg="red.50" color="red.600" />}
                      {reduzida && <StatusChip label="Reduzida" bg="orange.50" color="orange.700" />}
                      {!bloqueada && !reduzida && <StatusChip label="Regular" bg="green.50" color="green.700" />}
                      <HStack spacing={1.5} px="8px" py="3px" borderRadius="full" backgroundColor="gray.100">
                        <Text fontSize="11px" fontWeight="600" color="gray.700">
                          Código {conexao.codconexao ?? '—'}
                        </Text>
                      </HStack>
                      <HStack
                        spacing={1.5}
                        px="8px"
                        py="3px"
                        borderRadius="full"
                        backgroundColor={desteContrato ? `${colors.primaryColor}14` : 'gray.100'}
                      >
                        <Box boxSize="6px" borderRadius="full" backgroundColor={desteContrato ? colors.primaryColor : 'gray.400'} />
                        <Text fontSize="11px" fontWeight="600" color={desteContrato ? colors.primaryColor : 'gray.600'}>
                          Contrato {conexao.contrato ?? '—'}
                        </Text>
                      </HStack>
                    </HStack>
                    {conexao.motivo_bloqueio && (
                      <Text fontSize="12px" color="gray.600" marginTop="4px">
                        {conexao.motivo_bloqueio}
                      </Text>
                    )}
                  </Box>
                );
              })}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
